import React, { useMemo } from 'react';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useLocalStorage } from 'hooks/useLocalStorage';

// server grid is for grids whose pagination and filtering happen on the server
// the interface for the data grid is pretty different depending on which method is being used,
// so i'm keeping the functionality separate for now

// differences between server grid and non-server grid
// ** row count
// ** hardcoded page size (for now)
// ** page change
// ** pagination mode
// ** no export or filters
// ** different toolbar

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function CentsDataGridServer({
  getRowId,
  data,
  columns,
  onRowClick,
  rowCount,
  pageSize,
  onPageChange,
  page,
  isLoading,
  tableName,
  defaultColumnVisibilityModel,
  sortModel,
  onSortModelChange,
  setPageSize,
  pageSizeOptions,
  components,
  componentsProps,
  rowSelectionModel,
  onRowSelectionModelChange,
  checkboxSelection,
  getRowHeight,
  getRowClassName,
}) {
  const storageKey = `COLUMN_VISIBILITY_MODEL-${tableName}`;

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    storageKey,
    defaultColumnVisibilityModel,
  );

  const gridPageSizeOptions = useMemo(() => {
    if (!pageSizeOptions) return [pageSize];
    return pageSizeOptions.includes(pageSize)
      ? pageSizeOptions
      : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
  }, [pageSizeOptions, pageSize]);

  const dataGridCustomProperties = {};

  if (onSortModelChange) {
    dataGridCustomProperties.sortingMode = 'server';
    dataGridCustomProperties.onSortModelChange = onSortModelChange;
    if (sortModel) {
      dataGridCustomProperties.sortModel = sortModel;
    }
  }

  if (onRowClick) {
    dataGridCustomProperties.onRowClick = onRowClick;
  }

  const handlePaginationModelChange = (model) => {
    if (model.page !== page || model.pageSize !== pageSize) {
      onPageChange?.({ page: model.page, pageSize: model.pageSize });
    }
    if (setPageSize && model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  if (checkboxSelection) {
    dataGridCustomProperties.checkboxSelection = true;
  }

  if (onRowSelectionModelChange) {
    dataGridCustomProperties.onRowSelectionModelChange = onRowSelectionModelChange;
  }

  if (getRowHeight) {
    dataGridCustomProperties.getRowHeight = getRowHeight;
  }

  if (getRowClassName) {
    dataGridCustomProperties.getRowClassName = getRowClassName;
  }

  return (
    <div className="data-grid-container">
      <DataGrid
        components={{ ...components, Toolbar: CustomToolbar }}
        getRowId={getRowId}
        rows={data}
        columns={columns}
        density="compact"
        disableSelectionOnClick
        disableColumnMenu
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        pageSize={pageSize}
        pageSizeOptions={gridPageSizeOptions}
        rowSelectionModel={rowSelectionModel}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page },
          },
        }}
        onPaginationModelChange={handlePaginationModelChange}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        componentsProps={componentsProps}
        {...dataGridCustomProperties}
      />
    </div>
  );
}

CentsDataGridServer.propTypes = {
  tableName: PropTypes.string,
  // when we switch to typescript, we should use
  // the types from the library for the following prop types
  data: PropTypes.any,
  columns: PropTypes.any,
  onRowClick: PropTypes.func,
  rowCount: PropTypes.number,
  pageSize: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  isLoading: PropTypes.bool,
  defaultColumnVisibilityModel: PropTypes.any,
  sortModel: PropTypes.array,
  onSortModelChange: PropTypes.func,
  setPageSize: PropTypes.func,
  pageSizeOptions: PropTypes.array,
  getRowId: PropTypes.func,
  components: PropTypes.object,
  componentsProps: PropTypes.object,
  rowSelectionModel: PropTypes.array,
  onRowSelectionModelChange: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  getRowHeight: PropTypes.func,
  getRowClassName: PropTypes.func,
};
